/**
 * Remote debug logger — sends log events to the beta backend's /api/log endpoint.
 * Buffers events and flushes every 5 seconds or when buffer hits 50 entries.
 *
 * During startup, remote logging is always enabled so broken deploys are
 * visible. Once the app signals startup is complete (first gateway connection),
 * the user's localStorage preference is read and respected.
 *
 * When remote logging is enabled, console.log/warn/error are overridden to also
 * send to remote logging (issue #149).
 */

const LOG_FLUSH_INTERVAL = 5000;
const LOG_BUFFER_MAX = 50;
import { getApiUrl } from './environment';
import { getApiAuthHeaders } from './auth';

declare const __FRONTEND_VERSION__: string;

/** localStorage key for the user-facing toggle (default: enabled) */
export const DEBUG_LOGGING_KEY = 'voice-ai-debug-logging';

// ── Remote-logging gate ──
// Always enabled during startup; toggled by user preference after init.
let remoteLoggingEnabled = true;
let startupComplete = false;

// ── Console override ──
// Save originals before any override so we can restore them and avoid recursion.
const _origConsoleLog = console.log.bind(console);
const _origConsoleWarn = console.warn.bind(console);
const _origConsoleError = console.error.bind(console);
let consoleOverrideInstalled = false;

function formatArgs(args: unknown[]): string {
  return args
    .map((a) => (typeof a === 'object' ? JSON.stringify(a) : String(a)))
    .join(' ')
    .substring(0, 500);
}

function installConsoleOverride(): void {
  if (consoleOverrideInstalled) return;
  consoleOverrideInstalled = true;

  console.log = (...args: unknown[]) => {
    _origConsoleLog(...args);
    // Call _remoteLog directly to avoid recursion through the exported remoteLog
    _remoteLog('console', 'log', { msg: formatArgs(args) });
  };
  console.warn = (...args: unknown[]) => {
    _origConsoleWarn(...args);
    _remoteLog('console', 'warn', { msg: formatArgs(args) });
  };
  console.error = (...args: unknown[]) => {
    _origConsoleError(...args);
    _remoteLog('console', 'error', { msg: formatArgs(args) });
  };
}

function removeConsoleOverride(): void {
  if (!consoleOverrideInstalled) return;
  consoleOverrideInstalled = false;
  console.log = _origConsoleLog;
  console.warn = _origConsoleWarn;
  console.error = _origConsoleError;
}

/**
 * Called once when the app considers startup finished (e.g. first gateway
 * connection). From this point on, the user's localStorage preference is
 * respected.
 */
export function notifyStartupComplete(): void {
  if (startupComplete) return;
  startupComplete = true;
  const pref = localStorage.getItem(DEBUG_LOGGING_KEY);
  // Default is enabled; only disable if explicitly set to 'false'
  remoteLoggingEnabled = pref !== 'false';
  if (!remoteLoggingEnabled) {
    removeConsoleOverride();
  }
}

/**
 * Live-toggle called from the Settings UI. Only takes effect after startup.
 */
export function setRemoteLoggingEnabled(enabled: boolean): void {
  localStorage.setItem(DEBUG_LOGGING_KEY, String(enabled));
  if (startupComplete) {
    remoteLoggingEnabled = enabled;
    if (enabled) {
      installConsoleOverride();
    } else {
      removeConsoleOverride();
    }
  }
  // During startup we ignore the value — it will be picked up in notifyStartupComplete
}

/** Check current state (for Settings UI) */
export function isRemoteLoggingEnabled(): boolean {
  return remoteLoggingEnabled;
}

/** Readable device fingerprint generated once on module load */
const clientId = [
  /mobile/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
  `${screen.width}x${screen.height}`,
  navigator.platform || 'unknown',
].join('-');

interface LogEntry {
  ts: string;
  cat: string;
  evt: string;
  data: Record<string, unknown>;
}

const buffer: LogEntry[] = [];

/**
 * Internal log function — used by console override wrappers to avoid
 * going through the exported `remoteLog` (which calls console.log, causing recursion).
 */
function _remoteLog(category: string, event: string, data?: Record<string, unknown>): void {
  if (!remoteLoggingEnabled) return;

  buffer.push({
    ts: new Date().toISOString(),
    cat: category,
    evt: event,
    data: data ?? {},
  });

  if (buffer.length >= LOG_BUFFER_MAX) {
    flushLogs();
  }
}

/** Log an event (buffered, auto-flushed) */
export function remoteLog(category: string, event: string, data?: Record<string, unknown>): void {
  // Always log to console regardless of remote-logging preference
  // Use original console.log to avoid recursion when override is installed
  _origConsoleLog(`[${category}] ${event}`, data ?? '');

  _remoteLog(category, event, data);
}

function flushLogs(): void {
  if (buffer.length === 0) return;

  // Read auth fresh each flush (prefers JWT, falls back to legacy token)
  const authHeaders = getApiAuthHeaders('voice-ai-edge-tts-token');
  if (Object.keys(authHeaders).length === 0) return;

  const events = buffer.splice(0, LOG_BUFFER_MAX);
  const payload = JSON.stringify({
    events,
    ctx: { source: 'beta-native', clientId, ts: new Date().toISOString() },
  });

  const url = `${getApiUrl()}/api/log`;

  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: payload,
    keepalive: true,
    credentials: 'omit',
  }).catch(() => {});
}

// Auto-flush every 5 seconds
setInterval(flushLogs, LOG_FLUSH_INTERVAL);

// Flush on page hide (mobile backgrounding)
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushLogs();
    }
  });
}

// Global error handlers — send uncaught errors to remote log
window.onerror = (message, source, lineno, colno, error) => {
  remoteLog('error', 'uncaught_error', {
    message: String(message),
    source,
    lineno,
    colno,
    stack: error?.stack,
  });
};

window.onunhandledrejection = (event) => {
  remoteLog('error', 'unhandled_rejection', {
    reason: String(event.reason),
    stack: event.reason?.stack,
  });
};

// Install console override on module load (remote logging is enabled during startup)
installConsoleOverride();
